<template>
  <div>
    <b-modal
      id="modal-samples"
      ref="bModal"
      size="xl"
      :title="$t('component.samplesModal.title')"
      centered
      modal-class="modalSamples"
      style="z-index:10000"
      @show="onModalShow"
      @hide="fixFields = false; numberToCopy = 0"
    >
      <b-row class="px-2">
        <b-col>
          <div>
            <b-tabs
              ref="sampleTabs"
              v-model="selectedTabIndex"
              content-class="mt-3"
              class="vt-tabs samples-tabs line-tabs"
              @dblclick="editLabel"
            >
              <b-tab
                v-for="(sample, index) in samples"
                :key="index"
                lazy
                :title="sample.sampleNumber ? sample.sampleNumber : '*'"
                :title-link-class="{ errorTitle: (showErrors && (isEmpty(sample.topographicCode) || isEmpty(sample.sampleType))) }"
                @click="sampleIndexSelected = index"
              >
                <div v-if="selectedTabIndex === index">
                  <template slot="title">
                    {{ sample.sampleNumber ? sample.sampleNumber : '*' }}
                    <i
                      v-if="selectedFields !== 0"
                      class="ml-1 fal fa-clone"
                      aria-hidden="true"
                    />
                  </template>

                  <!--Sample Data-->
                  <b-row class="mt-3">
                    <b-col>
                      <div class="form-card pt-1">
                        <b-row>
                          <b-col class="d-flex align-items-center">
                            <span class="ap-title ap-title__section">
                              <i
                                class="far fa-prescription-bottle mr-2"
                                aria-hidden="true"
                              />{{ $t('component.samplesModal.dataSample') }}
                            </span>
                          </b-col>
                          <b-col>
                            <div class="text-right mb-2">
                              <b-button
                                v-if="$_canPrintCaseLabels()"
                                v-tooltip="$t('registry.requestGrid.contextMenu.printTag')"
                                qa="samples-form-print-labels-button"
                                class="btn__icon btn__icon--primary px-2"
                                :disabled="!$_canBeCaseLabelsPrinted(caseStudy)"
                              >
                                <i
                                  class="far fa-ticket-alt"
                                  aria-hidden="true"
                                />
                              </b-button>
                              <b-button
                                v-tooltip="$t('component.samplesModal.deleteSample')"
                                qa="samples-form-delete-button"
                                class="btn__icon btn__icon--primary px-2"
                                :disabled="$_disabledDeleteSamples(samples)"
                                @click="deleteSamplesBatch([sample])"
                              >
                                <i
                                  class="far fa-trash-alt"
                                  aria-hidden="true"
                                />
                              </b-button>
                            </div>
                          </b-col>
                        </b-row>
                        <b-row>
                          <b-col
                            lg="1"
                            sm="2"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label
                                  :class="{ requiredlabel: (showErrors && !sample.sampleNumber) }"
                                >
                                  {{ $t('component.samplesModal.id') }} *
                                </label>
                              </div>
                              <vt-input
                                id="sampleId"
                                v-model="sample.sampleNumber"
                                qa="samples-form-id-input"
                                class="form-control-sm"
                                name="sampleId"
                                :disabled="sample && sample.status && sample.status.code && sample.status.code !== sampleDraftCode"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="3"
                            sm="5"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label
                                  :class="{ requiredlabel: (showErrors && isEmpty(sample.topographicCode)) }"
                                >
                                  {{ $t('component.samplesModal.local') }} *
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-topographic-code-clone"
                                    field="topographicCode"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                v-model="sample.topographicCode"
                                v-validate="'required'"
                                qa="samples-form-topographic-code-select"
                                class="nowrap"
                                :disabled="$_disabledTopographicCode(sample)"
                                :class="{ requiredform: (showErrors && isEmpty(sample.topographicCode)) }"
                                :option-loader="() => loadTopographicCodes(sample)"
                                internal-search
                                label="description"
                                data-vv-name="topographicCode"
                                name="topographicCode"
                                data-vv-scope="samples"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="4"
                            sm="5"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label
                                  :class="{ requiredlabel: (showErrors && isEmpty(sample.sampleType)) }"
                                >
                                  {{ $t('component.samplesModal.type') }} *
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-sample-type-clone"
                                    field="sampleType"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                v-model="sample.sampleType"
                                v-validate="'required'"
                                qa="samples-form-sample-type-select"
                                :disabled="$_disabledSampleType(sample)"
                                :class="{ requiredform: (showErrors && isEmpty(sample.sampleType)) }"
                                :option-loader="() => loadSampleTypes(caseTypeId, sample)"
                                internal-search
                                :allow-empty="false"
                                track-by="id"
                                label="description"
                                data-vv-name="sampleType"
                                name="sampleType"
                                data-vv-scope="samples"
                                @input="onSelectSampleType(sample, $event.workflow)"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="4"
                            sm="6"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('global.procedure') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-procedure-clone"
                                    field="procedure"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                v-model="sample.procedure"
                                qa="samples-form-procedure-select"
                                class="nowrap"
                                :disabled="$_disabledProcedure(sample)"
                                :option-loader="() => loadProcedures(sample)"
                                internal-search
                                track-by="id"
                                label="description"
                                name="procedure"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="4"
                            sm="6"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('component.samplesModal.diagnosis') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-most-likely-clone"
                                    field="mostLikelyMorphologicCode"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                v-model="sample.mostLikelyMorphologicCode"
                                qa="samples-form-most-likely-select"
                                :disabled="$_disabledMostLikelyMorphologicCode(sample)"
                                :option-loader="() => loadMostLikelyMorphologicCodes(sample)"
                                track-by="id"
                                class="nowrap"
                                label="description"
                                name="mostLikelyMorphologicCode"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="2"
                            md="2"
                            sm="3"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('component.samplesModal.frag') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-fragments-clone"
                                    field="fragments"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-input
                                v-model="sample.fragments"
                                qa="samples-form-fragments-input"
                                class="form-control-sm"
                                :disabled="!$_isCaseEditable(caseStudy && caseStudy.status.code)"
                                name="fragments"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="3"
                            md="5"
                            sm="5"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('component.samplesModal.media') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-conservation-clone"
                                    :disabled="!fixFields"
                                    field="conservation"
                                  />
                                </label>
                              </div>
                              <vt-input
                                v-model="sample.conservation"
                                qa="samples-form-conservation-input"
                                class="form-control-sm"
                                name="conservation"
                                :disabled="!$_isCaseEditable(caseStudy && caseStudy.status.code)"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="3"
                            md="5"
                            sm="4"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('component.samplesModal.date') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-sampling-date-clone"
                                    field="samplingDate"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-date-picker
                                qa="samples-form-sampling-date"
                                :disabled="!$_isCaseEditable(caseStudy && caseStudy.status.code)"
                                name="samplingDate"
                                :not-after="new Date()"
                                :model="sample.samplingDate"
                                @update="sample.samplingDate = $event"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col lg="12">
                            <div class="form-label d-flex flex-column">
                              <label>{{ $t('global.observations') }}</label>
                              <b-form-textarea
                                v-model="sample.observations.text"
                                qa="samples-form-observations-textarea"
                                class="mb-2 ob-textarea"
                                max-rows="4"
                                :disabled="!$_isCaseEditable(caseStudy && caseStudy.status.code)"
                              />
                            </div>
                          </b-col>
                          <!-- Pending Re-Analysis -->
                          <!--                          <b-col lg="12">-->
                          <!--                            <div class="col-12 switch-btn p-0 mt-1 d-flex justify-content-end align-items-center">-->
                          <!--                              <div class="form-label py-2">-->
                          <!--                                <span class="mr-2">{{ $t('global.sampleOnSlide') }}</span>-->
                          <!--                                <input-->
                          <!--                                  id="sampleOnSlideToggle"-->
                          <!--                                  v-model="sample.sampleOnSlide"-->
                          <!--                                  :disabled="disabledSampleOnSlideSwitchButton(sample.id)"-->
                          <!--                                  class="offscreen"-->
                          <!--                                  type="checkbox"-->
                          <!--                                  @change="onSampleOnSlideToggleButtonChange(sample.id)"-->
                          <!--                                >-->
                          <!--                                <label-->
                          <!--                                  qa="modal-samples-clone-switch-label"-->
                          <!--                                  for="sampleOnSlideToggle"-->
                          <!--                                  class="switch"-->
                          <!--                                />-->
                          <!--                              </div>-->
                          <!--                            </div>-->
                          <!--                          </b-col>-->
                        </b-row>
                      </div>
                    </b-col>
                  </b-row>

                  <b-row class="mt-3">
                    <b-col>
                      <div class="form-card">
                        <span class="ap-title ap-title__section">
                          <i
                            class="far fa-user-friends mr-2"
                            aria-hidden="true"
                          />{{ $t('component.samplesModal.team') }}
                        </span>
                        <b-row class="mt-2">
                          <b-col
                            lg="4"
                            sm="6"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('registry.sampleGrid.grossingResponsible') }}
                                  <i
                                    v-if="sample.automaticGrossingResponsible"
                                    v-tooltip="$tc('global.automaticAssignment')"
                                    class="fal fa-magic ml-1"
                                    aria-hidden="true"
                                  />
                                  <vt-clone-toggle
                                    id="grossingResponsible"
                                    v-model="internalSelectedFields"
                                    qa="samples-form-grossing-responsible-clone"
                                    field="grossingResponsible"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                :key="sample.sampleTypeWorkflow"
                                v-model="sample.grossingResponsible"
                                qa="samples-form-grossing-responsible-select"
                                :disabled="$_disabledGrossingResponsible(sample)"
                                :option-loader="() => getPersonnelResponsible(sample)"
                                internal-search
                                name="grossingResponsible"
                                label="fullName"
                                @input="$forceUpdate()"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="4"
                            sm="6"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('registry.sampleGrid.grossingTech') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-grossing-controller-clone"
                                    field="grossingTechController"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                :key="sample.sampleTypeWorkflow"
                                v-model="sample.grossingTechController"
                                qa="samples-form-grossing-controller-select"
                                :disabled="$_disabledGrossingTechController(sample)"
                                :option-loader="() => getPersonnelControllers(sample)"
                                internal-search
                                name="grossingTechController"
                                label="fullName"
                                @input="$forceUpdate()"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="4"
                            sm="6"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('registry.sampleGrid.grossingTutor') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-grossing-tutor-clone"
                                    field="grossingTutor"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                :key="sample.sampleTypeWorkflow"
                                v-model="sample.grossingTutor"
                                qa="samples-form-grossing-tutor-select"
                                :disabled="$_disabledGrossingTutor(sample)"
                                :option-loader="() => getPersonnelTutors(sample)"
                                internal-search
                                name="grossingTutor"
                                label="fullName"
                                @input="$forceUpdate()"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            lg="4"
                            sm="6"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('registry.sampleGrid.grossingCoor') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-grossing-coordinator-clone"
                                    field="grossingCoordinator"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                :key="sample.sampleTypeWorkflow"
                                v-model="sample.grossingCoordinator"
                                qa="samples-form-grossing-coordinator-select"
                                :disabled="$_disabledGrossingCoordinator(sample)"
                                :option-loader="() => getPersonnelCoordinators(sample)"
                                internal-search
                                name="grossingCoordinator"
                                label="fullName"
                                @input="$forceUpdate()"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            v-if="sample.sampleTypeWorkflow && sample.sampleTypeWorkflow !== SAMPLE_TYPE_WORKFLOW.GROSSING_BIOPSIES.code"
                            lg="4"
                            sm="6"
                          >
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('component.samplesModal.specialist') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    qa="samples-form-specialist-clone"
                                    field="specialist"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                v-model="sample.specialist"
                                qa="samples-form-specialist-select"
                                :options="personnelCytologyExperts"
                                track-by="id"
                                :disabled="$_disabledSpecialist(sample)"
                                name="specialist"
                                label="fullName"
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>
                        <b-row class="d-none">
                          <b-col cols="12">
                            <hr>
                          </b-col>
                          <b-col>
                            <b-form-group>
                              <div class="form-label">
                                <label>{{ $t('component.samplesModal.pathologist') }}
                                  <vt-clone-toggle
                                    v-model="internalSelectedFields"
                                    field="pathologist"
                                    :disabled="!fixFields"
                                  />
                                </label>
                              </div>
                              <vt-combo-select
                                v-model="sample.pathologist"
                                :options="personnelPathologist"
                                :disabled="!$_isCaseEditable(caseStudy && caseStudy.status.code)"
                                track-by="id"
                                name="pathologist"
                                label="fullName"
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </div>
                    </b-col>
                  </b-row>
                </div>
              </b-tab>
              <div class="tab-btn">
                <b-row
                  class="p-0 m-0"
                  style="width:290px"
                >
                  <b-col class="p-0 m-0 mr-2">
                    <b-form-group>
                      <div class="col-12 switch-btn p-0 mt-1 d-flex justify-content-start">
                        <small class="mr-2">{{ $t('grossing.content.inputFix') }}</small>
                        <input
                          id="toggleSwitch"
                          v-model="fixFields"
                          class="offscreen"
                          :disabled="!$_isCaseEditable(caseStudy && caseStudy.status.code)"
                          type="checkbox"
                          aria-hidden="true"
                          @change="switchEvent()"
                        >
                        <label
                          qa="modal-samples-clone-switch-label"
                          for="toggleSwitch"
                          class="switch"
                        />
                      </div>
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="auto"
                    class="p-0 m-0"
                  >
                    <vt-input
                      v-model="cloneNumber"
                      qa="samples-form-number-to-clone-input"
                      class="form-control-sm"
                      placeholder="1"
                      style="width: 60px"
                      :min="1"
                      :max="100"
                      :disabled="disabledCloneNumberInput"
                      type="number"
                    />
                  </b-col>
                  <b-col
                    cols="auto"
                    class="mr-2 pl-0"
                  >
                    <vt-loading-button
                      qa="samples-form-clone-samples-button"
                      class="btn__outline btn-sm ml-1"
                      :disabled="disabledAddNewRowButton"
                      :params="{ promise: addNewSample }"
                    >
                      <i
                        class="fal fa-plus mr-2"
                        aria-hidden="true"
                      />{{ $tc('global.sample', 1) }}
                    </vt-loading-button>
                  </b-col>
                </b-row>
              </div>
            </b-tabs>
          </div>
        </b-col>
      </b-row>
      <div
        slot="modal-footer"
        class="footer-banner"
      >
        <div class="row">
          <div class="col-6 text-left">
            <b-button
              qa="samples-form-cancel"
              size="sm"
              variant="outline-secondary"
              data-dismiss="modal"
              @click="hide"
            >
              {{ $t('global.cancel') }}
            </b-button>
          </div>
          <div class="col-6 text-right">
            <vt-loading-button
              qa="samples-form-save"
              class="btn--primary btn-sm"
              :disabled="!$_isCaseEditable(caseStudy && caseStudy.status.code)"
              :params="{ promise: validateAndSave }"
            >
              <i
                class="far fa-save mr-2"
                aria-hidden="true"
              />{{ $t('global.save') }}
            </vt-loading-button>
          </div>
        </div>
      </div>
    </b-modal>
    <modal-confirmation
      id="modalSamplesConfirmationModal"
      ref="deleteModal"
      :content="deleteSampleModalConfirmationMessage"
    />
    <samples-update-util ref="samplesUpdateUtil" />
  </div>
</template>

<script>
import Encoder from '@/utils/encoder'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import samplesMixin from '@/modules/RegistryModule/views/editCase/mixins/samplesMixin'
import { cloneDeep, pick } from 'lodash'
import SamplesUpdateUtil from '@/components/common/SamplesUpdateUtil'
import { SAMPLE_TYPE_WORKFLOW } from '@/api/resources/common/commonSampleService'
import { SampleOperationGuardMixin } from '@/api/operations/mixins/guards/sample-operation-guard.mixin'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { SampleOperation } from '@/api/operations/models/sample-operation'
import { CASE_STATES } from '@/api/resources/entityState/case-state'

export default {
    name: 'ModalSamples',
    components: {
        SamplesUpdateUtil,
        ModalConfirmation
    },
    mixins: [
        samplesMixin,
        SampleOperationGuardMixin,
        CaseOperationGuardMixin
    ],
    props: {
        samplesArray: { type: Array },
        caseStudy: { type: Object },
        caseTypeId: { type: Number }
    },
    data() {
        return {
            addRowActive: true,
            componentCode: 'uicomponent.registry.fixsampleattributes',
            selectedFields: [],
            cloneNumber: 1,
            selectedTabIndex: 0,
            sampleIndexSelected: 0,
            sampleIndexToCopy: null,
            fixFields: false,
            showErrors: false,
            encoder: new Encoder(),
            samples: [],
            selected: null,
            formData: {
                caseId: null,
                grossingCoordinator: null,
                grossingResponsible: null,
                grossingTechController: null,
                grossingTutor: null,
                mostLikelyMorphologicCode: null,
                pathologist: null,
                procedure: null,
                conservation: null,
                fragments: null,
                observations: { text: '' },
                sampleNumber: null,
                samplingDate: null,
                virtualShelf: null,
                sampleType: null,
                specialist: null,
                topographicCode: null,
                sampleOnSlide: false
            }
        }
    },
    computed: {
        casId() { return this.caseStudy?.id || this.caseStudy?.casId },
        disabledCloneNumberInput() {
            // Pending https://soporte.vitro.bio/browse/APATH-6720
            // return !this.$_isCaseEditable(this.caseStudy.status?.code)
            return true
        },
        disabledAddNewRowButton() {
            return Number(this.cloneNumber) > 100 ||
                !this.$_isCaseEditable(this.caseStudy.status?.code) ||
                this.$_isCaseInAmendment(this.caseStudy.status?.code)
        },
        userCanDeleteSamples() {
            return this.hasOperationInCurrentLaboratoryGlobalMixin(SampleOperation.DELETE)
        },
        labId() {
            return this.$store.getters.labId
        },
        caseSamples() {
            return this.samplesArray
        },
        internalSelectedFields: {
            get() {
                if (this.fixFields) {
                    return this.selectedFields.slice()
                }
                return [].slice()
            },
            set(newValue) {
                this.selectedFields = newValue
                let toSave = { fields: this.selectedFields }
                this.customConfig = customConfigResource.save(this.componentCode, toSave)
            }
        },
        SAMPLE_TYPE_WORKFLOW() {
            return SAMPLE_TYPE_WORKFLOW
        },
        CASE_STATES() {
            return CASE_STATES
        },
        sampleDraftCode() {
            return CASE_STATES.DRAFT.code
        }
    },
    watch: {
        caseSamples(newValue) {
            this.samples = newValue
            this.cloneSamples(this.samples)
        }
    },
    async created() {
        let fixedFields = await customConfigResource.get(this.componentCode)
        this.selectedFields = fixedFields ? fixedFields.config.fields : []
    },
    methods: {
        disabledSampleOnSlideSwitchButton(sampleId) {
            return !sampleId
        },
        onSampleOnSlideToggleButtonChange(sampleId) {
            this.$emit('sampleOnSlideUpdate', sampleId)
        },
        onSelectSampleType(sample, newWorkflow) {
            /* TODO: Esperar a solucion para limpieza de responsables */
            /* if (sample.sampleTypeWorkflow !== newWorkflow) {
                this.cleanPersonnelsOfSample(sample)
            } */
            sample.sampleTypeWorkflow = newWorkflow
        },
        editLabel() {
            // TODO edit label
        },
        async onModalShow() {
            if (!this.caseSamples.length) {
                this.samples.push(cloneDeep(this.formData))
            } else {
                this.samples = this.caseSamples
                this.cloneSamples(this.samples)
            }
        },
        async validateAndSave() {
            let validationSamples = await this.$validator.validate()
            if (!validationSamples) {
                this.showErrors = true
                return
            }
            await this.saveSamples()
        },
        hide() {
            this.$refs.bModal.hide()
        },
        show() {
            this.$refs.bModal.show()
        },
        switchEvent() {
            if (this.fixFields) {
                this.sampleIndexToCopy = this.sampleIndexSelected
            }
        },
        async addNewSample() {
            if (Number(this.cloneNumber) + this.samples.length > 500) {
                this.$_showErrorModal(this.$tc('registry.sampleGrid.error.sampleAlreadySelected'))
            } else if (Number(this.cloneNumber) <= 100) {
                this.addRowActive = false
                const sampleNumberUsed = this.samples.map((item) => {
                    return item.sampleNumber
                })
                const [, response] = await this.toCall(this.getNextSamplesNumber(sampleNumberUsed, this.cloneNumber ? this.cloneNumber : 1))
                const newLabels = response.filter(x => !sampleNumberUsed.includes(x)).concat(sampleNumberUsed.filter(x => !response.includes(x)))
                let sampleToPush = null
                if (this.fixFields && this.selectedFields.length && this.sampleIndexToCopy != null) {
                    if (this.cloneNumber) {
                        for (let i = 0; i < this.cloneNumber; i++) {
                            sampleToPush = pick(this.samples[this.sampleIndexToCopy], this.selectedFields)
                            sampleToPush.observations = { text: '' }
                            sampleToPush.sampleNumber = newLabels[i]
                            sampleToPush.sampleOnSlide = false
                            this.samples.push(sampleToPush)
                            this.addRowActive = true
                        }
                        this.selectCreatedSample()
                        return
                    }
                    sampleToPush = pick(this.samples[this.sampleIndexToCopy], this.selectedFields)
                    sampleToPush.observations = { text: '' }
                    sampleToPush.sampleOnSlide = false
                    sampleToPush.sampleNumber = newLabels[0]
                    this.samples.push(sampleToPush)
                    this.selectCreatedSample()
                    this.addRowActive = true
                    return
                }
                sampleToPush = cloneDeep(this.formData)
                sampleToPush.sampleNumber = newLabels[0]
                this.samples.push(sampleToPush)
                this.selectCreatedSample()
                this.addRowActive = true
            }
        },
        selectCreatedSample() {
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.selectedTabIndex = this.samples.length - 1
                })
            })
        }
    }
}

</script>

<style lang="scss">

.modalSamples .ob-textarea{
  overflow-y: auto !important;
  font-size: font-size(very-small);
  height: 80px !important;
}

.modalSamples .modal-body{
  padding: 15px !important;
}

.errorTitle {
  color: red !important;
}

.vertical-line{
  width:1px;
  height: 92%;
  background:#dee2e6;
  display: inline-block;
}

.tab-btn{
  position: absolute;
  top: 0;
  right: 0;
}

.modalSamples .footer-banner{
  width: 100%;
}

.modalSamples .ag-grid-space{
  background-color: grey;
  max-height: 80vh;
  overflow-y:scroll;
}

.line-tabs .nav-tabs{
    width: 78% !important;
  }

@media (max-width: 1300px){

.mmodalSamples .footer-banner a{
  font-size: 0.9rem;
}

.links-modals a{
  font-size: 0.9rem !important;
}

.line-tabs .nav-tabs{
    width: 75% !important;
  }

}

</style>

<style lang="scss" scoped>

.fixed-fields{
  background: red;
}

  .switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  transition: all 0.3s;
}
.switch::after {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 18px;
  background-color: white;
  top: 1px;
  left: 1px;
  transition: all 0.3s;
}
input[type='checkbox']:checked + .switch::after {
  transform: translateX(20px);
}
input[type='checkbox']:checked + .switch {
  background-color: vt-color(primary);
}

.offscreen {
  position: absolute;
  left: -9999px;
}

  .form-space{
    margin-top: -10px !important;
  }

  .btn-space{
    margin-top: -10px !important;
  }

</style>
